import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { Users, Play, Trash2, ClipboardList, Ticket, CheckCircle, Sparkles, Search } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';
import './PhaseSetup.css';

export default function PhaseSetup({ tournament, tournaments, role, user }) {
    const [newPlayer, setNewPlayer] = useState('');
    const [bulkText, setBulkText] = useState('');
    const [showBulk, setShowBulk] = useState(false);
    const [basePointsData, setBasePointsData] = useState({});
    const [payingId, setPayingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        const unsubscribe = onSnapshot(bpRef, (docSnap) => {
            setBasePointsData(docSnap.exists() ? docSnap.data() : {});
        });
        return () => unsubscribe();
    }, []);

    // Calcula todos los jugadores que han jugado antes o que existen en la BD
    const globalPlayers = useMemo(() => {
        const set = new Set();
        tournaments.forEach(t => {
            if (t.id !== tournament.id) {
                (t.players || []).forEach(p => set.add(p.name.trim().toUpperCase()));
            }
        });
        Object.keys(basePointsData).forEach(k => set.add(k));
        return set;
    }, [tournaments, tournament.id, basePointsData]);

    const addPlayer = async (e) => {
        e.preventDefault();
        if (!newPlayer.trim()) return;
        const playerObj = { 
            id: crypto.randomUUID(), 
            name: newPlayer.trim(), 
            addedBy: user?.uid, 
            hasPaid: false,
            payment: null
        };
        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                transaction.update(tRef, { players: [...currentPlayers, playerObj] });
            });
            setNewPlayer('');
        } catch (err) {
            console.error("Error al añadir jugador en transacción:", err);
        }
    };

    const addBulkPlayers = async () => {
        if (!bulkText.trim() || role !== 'admin') return;
        
        const names = bulkText.split('\n').map(n => n.trim()).filter(n => n !== '');
        if (names.length === 0) return;

        const newPlayers = names.map(name => ({ 
            id: crypto.randomUUID(), 
            name, 
            addedBy: user?.uid, 
            hasPaid: false,
            payment: null
        }));

        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                transaction.update(tRef, { players: [...currentPlayers, ...newPlayers] });
            });
            setBulkText('');
            setShowBulk(false); 
        } catch (err) {
            console.error("Error al añadir lista de jugadores en transacción:", err);
        }
    };

    const removePlayer = async (playerId, addedBy) => {
        if (role !== 'admin' && addedBy !== user?.uid) return;
        if (!window.confirm("¿Seguro que quieres eliminar a este jugador de la inscripción?")) return;
        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                const updatedPlayers = currentPlayers.filter(p => p.id !== playerId);
                transaction.update(tRef, { players: updatedPlayers });
            });
        } catch (err) {
            console.error("Error al eliminar jugador en transacción:", err);
        }
    };

    const handlePayment = async (playerId, type, method, amount) => {
        if (role !== 'admin') return;
        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                const updatedPlayers = currentPlayers.map(p => 
                    p.id === playerId ? { ...p, payment: { type, method, amount }, hasPaid: true } : p
                );
                transaction.update(tRef, { players: updatedPlayers });
            });
            setPayingId(null);
        } catch (err) {
            console.error("Error al registrar pago en transacción:", err);
        }
    };

    const removePayment = async (playerId) => {
        if (role !== 'admin') return;
        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                const updatedPlayers = currentPlayers.map(p => 
                    p.id === playerId ? { ...p, payment: null, hasPaid: false } : p
                );
                transaction.update(tRef, { players: updatedPlayers });
            });
        } catch (err) {
            console.error("Error al anular pago en transacción:", err);
        }
    };

    const startPhase1 = async () => {
        if (role !== 'admin') return;
        if (tournament.players.length < 4) return window.alert("Se necesitan al menos 4 participantes.");

        const players = [...tournament.players];
        const isOdd = players.length % 2 !== 0;
        let matches = [];

        let entities = players.map(p => p.id);
        if (isOdd) entities.push('kubey');

        let previousMatchups = new Set();

        for (let round = 1; round <= 5; round++) {
            let roundMatches = null;
            let attempts = 0;
            const maxAttempts = 2000; 

            while (!roundMatches && attempts < maxAttempts) {
                attempts++;
                let currentRoundMatches = [];
                let available = [...entities].sort(() => 0.5 - Math.random());
                let validRound = true;

                while (available.length > 0) {
                    let p1 = available.pop();
                    let p2Index = -1;

                    for (let i = available.length - 1; i >= 0; i--) {
                        let candidate = available[i];
                        if (!previousMatchups.has(`${p1}_${candidate}`)) {
                            p2Index = i;
                            break;
                        }
                    }

                    if (p2Index !== -1) {
                        let p2 = available.splice(p2Index, 1)[0];
                        currentRoundMatches.push({ p1, p2 });
                    } else {
                        validRound = false;
                        break;
                    }
                }

                if (validRound) {
                    roundMatches = currentRoundMatches;
                }
            }

            if (!roundMatches) {
                console.warn(`Ronda ${round}: Imposible no repetir rival. Forzando emparejamiento de emergencia.`);
                let currentRoundMatches = [];
                let available = [...entities].sort(() => 0.5 - Math.random());
                while (available.length > 0) {
                    currentRoundMatches.push({ p1: available.pop(), p2: available.pop() });
                }
                roundMatches = currentRoundMatches;
            }

            roundMatches.forEach(pair => {
                previousMatchups.add(`${pair.p1}_${pair.p2}`);
                previousMatchups.add(`${pair.p2}_${pair.p1}`);

                let isKubey = pair.p1 === 'kubey' || pair.p2 === 'kubey';
                let finalP1 = pair.p1 === 'kubey' ? pair.p2 : pair.p1;
                let finalP2 = pair.p1 === 'kubey' ? pair.p1 : pair.p2;

                matches.push({
                    id: crypto.randomUUID(),
                    round,
                    p1: finalP1,
                    p2: finalP2,
                    p1Score: '',
                    p2Score: '',
                    isKubey,
                    status: 'pending',
                    isLocked: false
                });
            });
        }

        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { phase: 'phase1', phase1Matches: matches });
    };

    return (
        <div className="setup-container">
            <div className="setup-header">
                <h2 className="setup-title">
                    <Users style={{color: 'var(--accent-red)', width: '2rem', height: '2rem'}} /> Bladers Inscritos
                </h2>
                {role === 'admin' && (
                    <button onClick={() => setShowBulk(!showBulk)} className="btn-toggle-bulk">
                        <ClipboardList className="w-5 h-5" /> {showBulk ? 'Añadir 1 a 1' : 'Pegar Lista'}
                    </button>
                )}
            </div>
            
            <div className="setup-section">
                {!showBulk ? (
                    <form onSubmit={addPlayer} className="setup-form">
                        <input 
                            type="text" 
                            value={newPlayer} 
                            onChange={e => setNewPlayer(e.target.value)} 
                            className="setup-input" 
                            placeholder="Nombre del Blader..." 
                        />
                        <button className="btn-setup-add">
                            Añadir
                        </button>
                    </form>
                ) : (
                    role === 'admin' && (
                        <div className="setup-form" style={{flexDirection: 'column'}}>
                            <textarea
                                value={bulkText}
                                onChange={e => setBulkText(e.target.value)}
                                className="setup-textarea"
                                placeholder="Pega aquí la lista de bladers (uno por línea)...&#10;Ejemplo:&#10;JoseWiz&#10;Shurmiky&#10;OldPhone"
                            />
                            <button onClick={addBulkPlayers} className="btn-setup-add">
                                Inscribir Lista
                            </button>
                        </div>
                    )
                )}
            </div>

            {/* Buscador de jugadores */}
            {tournament.players.length > 0 && (
                <div className="setup-search-container">
                    <div className="search-input-wrapper">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar jugador..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="setup-input search-input"
                        />
                    </div>
                </div>
            )}

            <div className="players-grid">
                {tournament.players
                    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((p, idx) => {
                    const playerKey = p.name.trim().toUpperCase();
                    const playerBonos = basePointsData[playerKey]?.bonos || 0;
                    const isVeteran = globalPlayers.has(playerKey);

                    return (
                    <div key={p.id} className="player-setup-card">
                        
                        {/* Fila superior: Posición y Nombre */}
                        <div className="player-card-top">
                            <span className="player-index">#{idx + 1}</span> 
                            <div className="player-card-name-wrapper">
                                <span className="player-card-name text-gradient">
                                    {p.name}
                                </span>
                                {isVeteran ? (
                                    <CheckCircle className="w-4 h-4" style={{color: 'var(--success-color)', flexShrink: 0}} title="Blader verificado en la base de datos" />
                                ) : (
                                    <Sparkles className="w-4 h-4" style={{color: 'var(--accent-yellow)', flexShrink: 0}} title="Nuevo Blader en la liga" />
                                )}
                            </div>
                        </div>

                        {/* Fila inferior: Estados y Botones */}
                        <div className="player-card-bottom">
                            <div className="player-card-actions-row">
                                <div className="payment-status">
                                    {playerBonos > 0 ? (
                                        <span className="payment-badge bonus" title="Bono de torneos activo">
                                            <Ticket className="w-3 h-3" /> BONO ({playerBonos})
                                        </span>
                                    ) : p.payment ? (
                                        <span className="payment-badge paid">
                                            <span>{p.payment.type === 'child' ? '👦 Niño' : '🧑 Adulto'}</span>
                                            <span> - </span>
                                            <span>{p.payment.method === 'cash' ? '💵 Efectivo' : '📱 Bizum'} ({p.payment.amount}€)</span>
                                        </span>
                                    ) : p.hasPaid ? (
                                        <span className="payment-badge paid">PAGADO</span>
                                    ) : (
                                        <span className="payment-badge pending">Pendiente</span>
                                    )}
                                </div>
                                
                                <div className="action-buttons">
                                    {role === 'admin' && playerBonos === 0 && payingId !== p.id && (
                                        <button onClick={() => p.payment || p.hasPaid ? removePayment(p.id) : setPayingId(p.id)} className={`btn-collect ${p.payment || p.hasPaid ? 'is-paid' : 'is-pending'}`}>
                                            {p.payment || p.hasPaid ? 'Anular' : 'Cobrar'}
                                        </button>
                                    )}
                                    {(role === 'admin' || p.addedBy === user?.uid) && (
                                        <button onClick={() => removePlayer(p.id, p.addedBy)} className="btn-delete-player" title="Eliminar jugador">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            {/* Desplegable de cobro (solo admin) */}
                            {role === 'admin' && payingId === p.id && (
                                <div className="payment-options-dropdown">
                                    <div className="dropdown-title">Seleccionar tipo de entrada</div>
                                    <div className="payment-options-grid">
                                        <button onClick={() => handlePayment(p.id, 'child', 'cash', 2)} className="btn-payment-option cash">👦 2€ Efectivo</button>
                                        <button onClick={() => handlePayment(p.id, 'child', 'bizum', 2)} className="btn-payment-option bizum">👦 2€ Bizum</button>
                                        <button onClick={() => handlePayment(p.id, 'adult', 'cash', 3)} className="btn-payment-option cash">🧑 3€ Efectivo</button>
                                        <button onClick={() => handlePayment(p.id, 'adult', 'bizum', 3)} className="btn-payment-option bizum">🧑 3€ Bizum</button>
                                    </div>
                                    <button onClick={() => setPayingId(null)} style={{fontSize:'0.625rem', color:'var(--accent-red)', fontWeight:'900', textAlign:'right', marginTop:'0.25rem', background:'transparent', border:'none', cursor:'pointer', textTransform:'uppercase'}}>Cancelar</button>
                                </div>
                            )}
                        </div>

                    </div>
                    );
                })}
                {tournament.players.length === 0 ? (
                    <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '2.5rem 0', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase', fontStyle: 'italic', letterSpacing: '2px'}}>
                        Esperando bladers...
                    </div>
                ) : tournament.players.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                    <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '2.5rem 0', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase', fontStyle: 'italic', letterSpacing: '2px'}}>
                        No se encontraron bladers.
                    </div>
                )}
            </div>

            {role === 'admin' && tournament.players.length >= 4 && (
                <button onClick={startPhase1} className="btn-start-phase">
                    <Play style={{fill: 'var(--bg-color)', width: '1.5rem', height: '1.5rem'}}/> Iniciar Ronda Suiza
                </button>
            )}
        </div>
    );
}